/**
 * PageAccessRow - Single page access toggle row with role badge
 */

import { Lock, Unlock, Crown, Shield, Users, Star, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { NavAccessLevel } from '../types'

export function getRoleIcon(role: NavAccessLevel) {
  switch (role) {
    case 'owner': return Crown
    case 'admin': return Shield
    default: return Users
  }
}

export function getRoleColor(role: NavAccessLevel) {
  switch (role) {
    case 'owner': return 'text-amber-600 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400'
    case 'admin': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400'
    case 'member': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400'
    default: return 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400'
  }
}

interface PageAccessRowProps {
  page: { route: string; label: string; accessLevel: NavAccessLevel; premiumOnly?: boolean }
  isAllowed: boolean
  canManage: boolean
  onToggle: () => void
}

export function PageAccessRow({ page, isAllowed, canManage, onToggle }: PageAccessRowProps) {
  const RoleIcon = getRoleIcon(page.accessLevel)

  return (
    <div className={cn(
      'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors',
      isAllowed ? 'bg-white dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/30'
    )}>
      <div className="flex-shrink-0">
        {isAllowed ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium truncate', isAllowed ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500')}>{page.label}</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{page.route}</p>
      </div>
      <div className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium', getRoleColor(page.accessLevel))}>
        <RoleIcon className="w-3 h-3" />
        <span className="capitalize">{page.accessLevel}</span>
      </div>
      {page.premiumOnly && (
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
          <Star className="w-3 h-3" /><span>PRO</span>
        </div>
      )}
      {canManage && (
        <button onClick={onToggle} className={cn(
          'flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
          isAllowed ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
        )}>
          {isAllowed ? <><Unlock className="w-3 h-3" /> Allowed</> : <><Lock className="w-3 h-3" /> Blocked</>}
        </button>
      )}
    </div>
  )
}
